/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable react-hooks/exhaustive-deps */
import {
  CheckCircleOutlined,
  ContainerOutlined,
  FileTextOutlined,
  LoadingOutlined,
  PlayCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { Button, Empty, message, Modal, Radio, Spin } from 'antd';
import CycleCard from 'components/cycleCard';
import Header from 'components/header';
import RenameCycleModal from 'components/renameCycleModal';
import Sidebar from 'components/sidebar';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  getCycleDetails,
  listCycles,
  toggleCycleArchived,
} from 'services/api/cycles';
import styles from 'styles/cycles.module.scss';
import { CYCLE_STATUS } from 'utils/constants';
import { getCreatedDateFromMongoId } from 'utils/utils';
import DuplicateCycleModal from 'components/duplicateCycleModal';

const VALID_CYCLE_TYPES = ['live', 'drafts', 'done', 'archived'];

const Cycles = () => {
  const [cycles, setCycles] = useState([]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const typeFromUrl = searchParams.get('type');
  const initialType =
    typeFromUrl && VALID_CYCLE_TYPES.includes(typeFromUrl) ? typeFromUrl : 'live';
  const [loading, setLoading] = useState(false);
  const [renameCycle, setRenameCycle] = useState(null);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [selectedCycleTypes, setSelectedCycleTypes] = useState(initialType);
  const [selectedCycles, setSelectedCycles] = useState([]);
  const [activeCyclesLive, setActiveCyclesLive] = useState([]);
  const [activeCyclesDrafts, setActiveCyclesDrafts] = useState([]);
  const [activeCyclesDone, setActiveCyclesDone] = useState([]);
  const [archivedCycles, setArchivedCycles] = useState([]);
  const [duplicateLoading, setDuplicateLoading] = useState(false);
  const [duplicateModalVisible, setDuplicateModalVisible] = useState(false);
  const [duplicateCycleDetails, setDuplicateCycleDetails] = useState(null);

  const fetchCycles = () => {
    if (loading) return;
    setLoading(true);
    listCycles()
      .then((res) => {
        if (res.data.length === 0) {
          navigate('/onboarding');
        }
        const allCycles = res.data
          .map((c) => ({
            ...c,
            createdAt: getCreatedDateFromMongoId(c._id),
          }))
          .sort((a, b) => b.createdAt - a.createdAt);
        setCycles(allCycles);

        const activeCyclesLive = allCycles.filter(
          (cycle) =>
            !cycle.isArchived &&
            cycle.status !== CYCLE_STATUS.DRAFT &&
            dayjs(cycle.deadline) > dayjs()
        );
        setActiveCyclesLive(activeCyclesLive);

        const activeCyclesDrafts = allCycles.filter(
          (cycle) => !cycle.isArchived && cycle.status === CYCLE_STATUS.DRAFT
        );
        setActiveCyclesDrafts(activeCyclesDrafts);

        const activeCyclesDone = allCycles.filter(
          (cycle) =>
            !cycle.isArchived &&
            cycle.status !== CYCLE_STATUS.DRAFT &&
            dayjs(cycle.deadline) < dayjs()
        );
        setActiveCyclesDone(activeCyclesDone);

        const archivedCycles = allCycles.filter((cycle) => cycle.isArchived);
        setArchivedCycles(archivedCycles);

        const cyclesByType = {
          live: activeCyclesLive,
          drafts: activeCyclesDrafts,
          done: activeCyclesDone,
          archived: archivedCycles,
        };
        const typeFromUrlNow = searchParams.get('type');
        const hasValidTypeFromUrl =
          typeFromUrlNow && VALID_CYCLE_TYPES.includes(typeFromUrlNow);
        const typeToUse = hasValidTypeFromUrl
          ? typeFromUrlNow
          : VALID_CYCLE_TYPES.find((t) => cyclesByType[t].length > 0) ?? 'live';

        setSelectedCycleTypes(typeToUse);
        setSelectedCycles(cyclesByType[typeToUse]);
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchCycles();
  }, []);

  const createNewCycle = () => {
    navigate('/create-cycle');
  };

  const onArchive = (cycle) => {
    return Modal.confirm({
      title: cycle.isArchived ? 'Unarchive Cycle' : 'Archive Cycle',
      content: `Are you sure you want to ${
        cycle.isArchived ? 'unarchive' : 'archive'
      } this cycle?`,
      onOk: () => {
        setArchiveLoading(true);
        toggleCycleArchived({ cycleId: cycle._id })
          .then(() => {
            fetchCycles();
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            setArchiveLoading(false);
          });
      },
      okText: `Yes, ${cycle.isArchived ? 'Unarchive' : 'Archive'}`,
      cancelText: 'No, Cancel',
      okButtonProps: {
        loading: archiveLoading,
      },
    });
  };

  const closeDuplicateModal = () => {
    setDuplicateModalVisible(false);
    setDuplicateCycleDetails(null);
  };

  const onDuplicate = (c) => {
    setDuplicateLoading(true);
    getCycleDetails(c._id)
      .then((res) => {
        const cycleDetails = res.data;
        setDuplicateCycleDetails(cycleDetails);
        setDuplicateModalVisible(true);
      })
      .catch((err) => {
        console.error('error getting cycle details', err);
        message.error('Error getting cycle details');
      })
      .finally(() => {
        setDuplicateLoading(false);
      });
    //create a new cycle with the same modules and blocks
  };

  useEffect(() => {
    if (selectedCycleTypes === 'live') {
      setSelectedCycles(activeCyclesLive);
    } else if (selectedCycleTypes === 'drafts') {
      setSelectedCycles(activeCyclesDrafts);
    } else if (selectedCycleTypes === 'done') {
      setSelectedCycles(activeCyclesDone);
    } else if (selectedCycleTypes === 'archived') {
      setSelectedCycles(archivedCycles);
    }
  }, [selectedCycleTypes]);

  // const selectedCycles =
  return (
    <div className={styles.dashboard}>
      <Header />
      <div className={styles.dashboardWrapper}>
        <Sidebar />
        <div className={styles.content}>
          <div className={styles.inner}>
            <div className={styles.header}>
              {/* <Breadcrumb
                items={[
                  {
                    title: (
                      <a href="/#/dashboard">
                        <HomeOutlined />
                        &nbsp;Dashboard
                      </a>
                    ),
                  },
                ]}
              /> */}
              <div className={styles.headerContent}>
                <div className={styles.headerTitle}>
                  <h1>Cycles</h1>
                </div>
                <div className={styles.headerActions}>
                  <Button
                    icon={<PlusOutlined />}
                    type={'primary'}
                    onClick={createNewCycle}
                  >
                    Create new cycle
                  </Button>
                </div>
              </div>
            </div>
            <div className={styles.cycles}>
              {loading && (
                <div
                  style={{
                    textAlign: 'center',
                    marginTop: 24,
                    marginBottom: 24,
                  }}
                >
                  <Spin
                    spinning={loading}
                    indicator={
                      <LoadingOutlined spin style={{ fontSize: 44 }} />
                    }
                  />
                </div>
              )}
              {!loading && cycles && cycles.length === 0 ? (
                <div className={styles.noCycles}>
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <div>
                        <h1>No cycles found</h1>
                        <p>Create a new cycle to get started</p>
                        <Button
                          icon={<PlusOutlined />}
                          type={'primary'}
                          onClick={createNewCycle}
                        >
                          Create new cycle
                        </Button>
                      </div>
                    }
                  />
                </div>
              ) : (
                <div>
                  <Radio.Group
                    value={selectedCycleTypes}
                    optionType="button"
                    buttonStyle="solid"
                    onChange={(e) => {
                      const value = e.target.value;
                      setSelectedCycleTypes(value);
                      setSearchParams({ type: value });
                    }}
                  >
                    <Radio.Button value="live">
                      <PlayCircleOutlined />
                      &nbsp; Live
                      {activeCyclesLive ? ` (${activeCyclesLive.length})` : ''}
                    </Radio.Button>
                    <Radio.Button value="drafts">
                      <FileTextOutlined />
                      &nbsp; Drafts
                      {activeCyclesDrafts
                        ? ` (${activeCyclesDrafts.length})`
                        : ''}
                    </Radio.Button>
                    <Radio.Button value="done">
                      <CheckCircleOutlined />
                      &nbsp; Done
                      {activeCyclesDone ? ` (${activeCyclesDone.length})` : ''}
                    </Radio.Button>
                    <Radio.Button value="archived">
                      <ContainerOutlined />
                      &nbsp; Archived
                      {archivedCycles ? ` (${archivedCycles.length})` : ''}
                    </Radio.Button>
                  </Radio.Group>
                  <div style={{ marginTop: 44 }}>
                    {selectedCycles.map((cycle) => (
                      <CycleCard
                        key={cycle._id}
                        cycle={cycle}
                        onRename={() => setRenameCycle(cycle)}
                        onArchive={() => onArchive(cycle)}
                        onDuplicate={() => onDuplicate(cycle)}
                        duplicateLoading={duplicateLoading}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <RenameCycleModal
        cycle={renameCycle}
        open={!!renameCycle}
        onClose={() => {
          setRenameCycle(null);
          fetchCycles();
        }}
      />
      <DuplicateCycleModal
        open={duplicateModalVisible}
        cycle={duplicateCycleDetails}
        onClose={closeDuplicateModal}
        onDuplicated={(newCycle) => {
          navigate(`/cycles/${newCycle._id}`);
        }}
        onLoadingChange={(loading) => setDuplicateLoading(loading)}
      />
    </div>
  );
};

export default Cycles;
